<script lang="ts">
	import { format } from 'svelte-i18n';
	import { beforeUpdate, onDestroy } from 'svelte';
	import { tweened } from 'svelte/motion';
	import type { Device } from 'src/types/Device';
	import type { LightMode } from 'src/types/LightType';
	import { user_data } from 'src/stores/account/user';
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import {
		claimLightPriorityDevHud,
		lightPriorityDevForce,
		lightPriorityDevLayout,
	} from 'src/stores/features/dev/light-priority-dev-state';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import LightModeRail from 'src/components/Sidepanel/DeviceViews/Light/LightModeRail.svelte';
	import NextScheduleCard from 'src/components/Sidepanel/DeviceViews/NextScheduleCard.svelte';
	import {
		Button,
		ButtonGroupV2,
		ColorSlider,
		ControlCard,
		DeviceButtonV2,
		Icon,
		PriorityGrid,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isDevModalAvailable, isNonProductionAppBuild } from 'src/helpers/dev_tools';
	import { deviceControlOnOff } from 'src/helpers/device_control_on_off';
	import {
		get_gen_2_control_component_channel_structure,
		get_z_wave_channel_structure_from_category,
		is_gen_2,
		is_gen_z,
		real_id,
	} from 'src/helpers/interface_helpers';
	import {
		DIMMER_PREVIEW_RGB,
		HUE_GRADIENT,
		WARMTH_GRADIENT,
		ctToPreviewRgb,
		hueSaturationToRgb,
		rgbCss,
		rgbToHueSat,
	} from 'src/helpers/light_color_math';
	import {
		readLightChannel,
		readLightCt,
		readLightOn,
		readLightRgb,
		setLightChannel,
		setLightCt,
		setLightRgb,
	} from 'src/helpers/light_color_commands';
	import { changeLightMode } from 'src/helpers/light_mode_change';
	import {
		applyForcedLightCapabilities,
		applyForcedLightPower,
		inferLightOverlay,
		resolveLightMixerChannels,
		resolveLightPriorityCapabilities,
	} from 'src/helpers/light_priority_layout';
	import {
		interpolateLightSliderList,
		LIGHT_SLIDER_EASING,
		lightSliderDisplayTarget,
		lightSliderListEquals,
		lightSliderTweenDuration,
		shouldApplyLightStatusField,
		shouldTweenLightSlider,
		shouldTweenLightSliderList,
		type LightSliderHold,
	} from 'src/helpers/light_slider_motion';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { prefersReducedMotion } from 'src/helpers/ui_transitions';
	import { calculate_timer, clear_device_timer_state } from 'src/helpers/timer_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device: Device;
	export let brightnessDisabled = false;

	let releaseDevHud: (() => void) | undefined;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: uiStateStore = getDeviceState(device?.id);
	$: uiState = $uiStateStore;
	$: liveCaps = resolveLightPriorityCapabilities(device, status, settings);
	$: layoutForce =
		$lightPriorityDevForce === 'calibrating' && $lightPriorityDevLayout === 'auto'
			? 'dimmer'
			: $lightPriorityDevLayout;
	$: caps = applyForcedLightCapabilities(liveCaps, layoutForce);
	$: liveMixerChannels = resolveLightMixerChannels(device, status, settings, $devices);
	$: mixerChannels = layoutForce === 'auto' || layoutForce === 'dimmer' ? liveMixerChannels : [];
	$: isMixer = mixerChannels.length > 1;
	$: isDimmer = caps.kind === 'dimmer';
	$: sliderProp = caps.sliderProp || 'brightness';
	$: hasRights = hasControlRights(device);
	$: overlay = inferLightOverlay({
		online: Boolean(status?.online),
		force: $lightPriorityDevForce,
		uiStateType: typeof uiState?.type === 'number' ? uiState.type : undefined,
	});
	$: islandDisabled = Boolean(overlay);

	let hue = 18;
	let saturation = 72;
	let ct = 4000;
	let brightness = 80;
	let white = 40;
	let ison = true;
	let mixerBrightness: number[] = [];
	let mixerOn: boolean[] = [];
	let powerLoading = false;
	let modeLoading = false;
	let displayedColorType: LightMode = 'color';
	let sliderHold: LightSliderHold | undefined;
	let sliderCommitSeq = 0;
	let dragging = '';
	let pinLocked = false;
	let showPinModal = false;
	let pendingAction: (() => Promise<void>) | undefined;
	let hasTimer = false;
	let timer = { time: 0, percentage: 100 };
	let motionDeviceId = '';
	let brightnessSliderPrimed = false;
	let mixerSliderPrimed = false;
	let lastBrightnessTarget = 0;
	let lastMixerTargets: number[] = [];
	let cardAccent = '';
	$: if (!modeLoading) displayedColorType = caps.colorType;

	const displayedBrightness = tweened(0, {
		duration: 0,
		easing: LIGHT_SLIDER_EASING,
	});
	const displayedMixerBrightness = tweened<number[]>([], {
		duration: 0,
		easing: LIGHT_SLIDER_EASING,
		interpolate: interpolateLightSliderList,
	});

	$: controlId = `light-priority-${real_id(device?.id || 'device')}`;
	$: displayIson = applyForcedLightPower(ison, $lightPriorityDevForce);
	$: displayMixerOn = mixerOn.map((on) => applyForcedLightPower(on, $lightPriorityDevForce));
	$: onCount = isMixer ? displayMixerOn.filter(Boolean).length : displayIson ? 1 : 0;
	$: anyOn = isMixer ? onCount > 0 : displayIson;
	$: allOn = isMixer ? mixerChannels.length > 0 && onCount === mixerChannels.length : displayIson;
	$: mixedPower = isMixer && onCount > 0 && onCount < mixerChannels.length;
	$: railsDisabled = islandDisabled || !displayIson || !hasRights || brightnessDisabled;
	$: hueAccent = `hsl(${hue} 100% 50%)`;
	$: saturationGradient = `linear-gradient(90deg, rgb(255 255 255), ${hueAccent})`;
	$: baseColor = isDimmer
		? DIMMER_PREVIEW_RGB
		: caps.showCct
			? ctToPreviewRgb(ct, caps.ctRange.minValue, caps.ctRange.maxValue)
			: hueSaturationToRgb(hue, saturation);
	$: accent = rgbCss(baseColor);
	$: if (accent !== cardAccent) cardAccent = accent;
	$: brightnessGradient = `linear-gradient(90deg, transparent, ${accent})`;
	$: brightnessTarget = lightSliderDisplayTarget(displayIson, brightness, dragging === 'bri');
	$: mixerTargets = mixerChannels.map((_, index) =>
		lightSliderDisplayTarget(
			Boolean(displayMixerOn[index]),
			mixerBrightness[index] || 0,
			dragging === `bri-${index}`,
		),
	);
	$: mixerDragging = dragging.startsWith('bri-');
	$: showDevHud = Boolean(isNonProductionAppBuild() && isDevModalAvailable($user_data?.email));

	const syncDevHud = (open: boolean) => {
		if (open) {
			if (!releaseDevHud) releaseDevHud = claimLightPriorityDevHud();
		} else if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
		}
	};

	$: syncDevHud(showDevHud);

	onDestroy(() => {
		if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
		}
	});

	const patchOutput = (target: Device, nextStatus: Record<string, any>, nextOn: boolean) => {
		if (is_gen_z(target)) {
			const path = get_z_wave_channel_structure_from_category(target);
			const nextBlock = { ...(nextStatus[path] || {}) };
			if (typeof nextBlock.output !== 'undefined') {
				nextBlock.output = nextOn;
			} else if (typeof nextBlock.on !== 'undefined') {
				nextBlock.on = nextOn;
			} else if (typeof nextBlock.currentValue === 'boolean') {
				nextBlock.currentValue = nextOn;
			} else {
				nextBlock.output = nextOn;
			}
			if (typeof nextBlock.ison !== 'undefined') nextBlock.ison = nextOn;
			return { ...nextStatus, [path]: nextBlock };
		}
		if (is_gen_2(target)) {
			const path = get_gen_2_control_component_channel_structure(target);
			if (nextStatus[path]) {
				return {
					...nextStatus,
					[path]: {
						...nextStatus[path],
						...(typeof nextStatus[path].output !== 'undefined'
							? { output: nextOn }
							: { on: nextOn }),
					},
				};
			}
		} else if (nextStatus.lights?.[target.channel]) {
			return {
				...nextStatus,
				lights: nextStatus.lights.map((light, index) =>
					index === target.channel ? { ...light, ison: nextOn } : light,
				),
			};
		}
		return nextStatus;
	};

	const resetSliderMotion = (id: string | undefined) => {
		const nextId = id || '';
		if (nextId === motionDeviceId) return;
		const switching = motionDeviceId !== '';
		motionDeviceId = nextId;
		brightnessSliderPrimed = false;
		mixerSliderPrimed = false;
		lastBrightnessTarget = 0;
		lastMixerTargets = [];
		if (!switching) return;
		sliderHold = undefined;
		sliderCommitSeq += 1;
		displayedBrightness.set(0, { duration: 0 });
		displayedMixerBrightness.set([], { duration: 0 });
	};

	const applyBrightnessMotion = (target: number, isDragging: boolean) => {
		if (!brightnessSliderPrimed || target === lastBrightnessTarget) return;
		const animate = shouldTweenLightSlider({
			from: lastBrightnessTarget,
			to: target,
			primed: true,
			dragging: isDragging,
		});
		displayedBrightness.set(target, {
			duration: lightSliderTweenDuration(animate, prefersReducedMotion()),
		});
		lastBrightnessTarget = target;
	};

	const applyMixerMotion = (targets: number[], isDragging: boolean) => {
		if (!mixerSliderPrimed || lightSliderListEquals(targets, lastMixerTargets)) return;
		const animate = shouldTweenLightSliderList(lastMixerTargets, targets, true, isDragging);
		displayedMixerBrightness.set(targets, {
			duration: lightSliderTweenDuration(animate, prefersReducedMotion()),
		});
		lastMixerTargets = [...targets];
	};

	const primeSliderMotion = () => {
		if (!brightnessSliderPrimed) {
			const target = lightSliderDisplayTarget(ison, brightness, false);
			displayedBrightness.set(target, { duration: 0 });
			lastBrightnessTarget = target;
			brightnessSliderPrimed = true;
		}
		if (!isMixer || mixerSliderPrimed) return;
		const targets = mixerChannels.map((_, index) =>
			lightSliderDisplayTarget(Boolean(mixerOn[index]), mixerBrightness[index] || 0, false),
		);
		displayedMixerBrightness.set(targets, { duration: 0 });
		lastMixerTargets = [...targets];
		mixerSliderPrimed = true;
	};

	$: resetSliderMotion(device?.id);
	$: applyBrightnessMotion(brightnessTarget, dragging === 'bri');
	$: applyMixerMotion(mixerTargets, mixerDragging);

	const beginSliderHold = (hold: LightSliderHold) => {
		sliderCommitSeq += 1;
		sliderHold = hold;
		dragging = '';
		return sliderCommitSeq;
	};

	const endSliderHold = (runId: number) => {
		if (runId === sliderCommitSeq) sliderHold = undefined;
	};

	const syncFromStatus = () => {
		if (!status || dragging || powerLoading || modeLoading) return;
		ison = readLightOn(device, status);
		if (isMixer) {
			mixerOn = mixerChannels.map((channel) => readLightOn(channel.device, status));
			mixerBrightness = mixerChannels.map((channel, index) => {
				if (!shouldApplyLightStatusField(sliderHold, 'mixer-brightness', index)) {
					return mixerBrightness[index] || 0;
				}
				const value = readLightChannel(channel.device, status, sliderProp);
				return typeof value === 'number' ? value : 80;
			});
		}
		const nextRgb = readLightRgb(device, status);
		if (nextRgb && caps.showRgb && shouldApplyLightStatusField(sliderHold, 'hue-sat')) {
			const hs = rgbToHueSat(nextRgb);
			hue = hs.h;
			saturation = hs.s;
		}
		const nextCt = readLightCt(device, status);
		if (typeof nextCt === 'number' && shouldApplyLightStatusField(sliderHold, 'ct')) {
			ct = nextCt;
		}
		const nextBrightness = readLightChannel(device, status, sliderProp);
		if (
			typeof nextBrightness === 'number' &&
			shouldApplyLightStatusField(sliderHold, 'brightness')
		) {
			brightness = nextBrightness;
		}
		const nextWhite = readLightChannel(device, status, 'white', true);
		if (typeof nextWhite === 'number' && shouldApplyLightStatusField(sliderHold, 'white')) {
			white = nextWhite;
		}

		const component = is_gen_2(device) ? get_gen_2_control_component_channel_structure(device) : '';
		hasTimer = is_gen_2(device)
			? typeof status?.[component]?.timer_started_at !== 'undefined'
			: Boolean(status?.lights?.[device.channel]?.has_timer);
		if (hasTimer && settings) {
			const result = calculate_timer(device, status, settings);
			timer = { time: Number(result?.[1]) || 0, percentage: Number(result?.[2]) || 100 };
		} else {
			timer = { time: 0, percentage: 100 };
		}
		primeSliderMotion();
	};

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		syncFromStatus();
	});

	const runOrUnlock = async (action: () => Promise<void>) => {
		if (pinLocked) {
			pendingAction = action;
			showPinModal = true;
			dragging = '';
			return;
		}
		await action();
	};

	const handlePinUnlock = async () => {
		pinLocked = unlockPin(device, pinLocked);
		showPinModal = false;
		const action = pendingAction;
		pendingAction = undefined;
		if (action) await action();
	};

	const commitHueSat = async (nextHue: number, nextSat: number) => {
		hue = nextHue;
		saturation = nextSat;
		const runId = beginSliderHold({ field: 'hue-sat', hue: nextHue, sat: nextSat });
		if (!status) {
			endSliderHold(runId);
			return;
		}
		try {
			await setLightRgb(device, status, hueSaturationToRgb(nextHue, nextSat));
		} finally {
			endSliderHold(runId);
		}
	};

	const commitCt = async (nextCt: number) => {
		ct = nextCt;
		const runId = beginSliderHold({ field: 'ct', value: nextCt });
		if (!status) {
			endSliderHold(runId);
			return;
		}
		try {
			await setLightCt(device, status, nextCt);
		} finally {
			endSliderHold(runId);
		}
	};

	const commitBrightness = async (nextValue: number) => {
		brightness = nextValue;
		const runId = beginSliderHold({ field: 'brightness', value: nextValue });
		if (!status) {
			endSliderHold(runId);
			return;
		}
		try {
			await setLightChannel(device, status, sliderProp, nextValue);
		} finally {
			endSliderHold(runId);
		}
	};

	const commitMixerBrightness = async (index: number, nextValue: number) => {
		mixerBrightness[index] = nextValue;
		mixerBrightness = [...mixerBrightness];
		const runId = beginSliderHold({ field: 'mixer-brightness', index, value: nextValue });
		const channel = mixerChannels[index];
		if (!status || !channel) {
			endSliderHold(runId);
			return;
		}
		try {
			await setLightChannel(channel.device, status, sliderProp, nextValue);
		} finally {
			endSliderHold(runId);
		}
	};

	const commitWhite = async (nextValue: number) => {
		white = nextValue;
		const runId = beginSliderHold({ field: 'white', value: nextValue });
		if (!status) {
			endSliderHold(runId);
			return;
		}
		try {
			await setLightChannel(device, status, 'white', nextValue, true);
		} finally {
			endSliderHold(runId);
		}
	};

	const handleMode = async (next: 'color' | 'white') => {
		if (islandDisabled || caps.colorType === next || modeLoading || powerLoading || !hasRights) {
			return;
		}
		await runOrUnlock(async () => {
			modeLoading = true;
			try {
				await changeLightMode(device, settings, next);
			} finally {
				modeLoading = false;
			}
		});
	};

	const applyTurnResult = (target: Device, nextOn: boolean) => {
		if (!status) return;
		const nextStatus = patchOutput(target, status, nextOn);
		clear_device_timer_state(nextStatus, target);
		hasTimer = false;
		devices_status.update_device_status({
			id: real_id(target.id),
			msg: nextStatus,
		});
	};

	const handlePower = async () => {
		if (islandDisabled || powerLoading || modeLoading || !hasRights) return;
		await runOrUnlock(async () => {
			powerLoading = true;
			try {
				if (isMixer) {
					const turnOn = !anyOn;
					let nextStatus = status;
					let failed = false;
					for (let index = 0; index < mixerChannels.length; index++) {
						const channel = mixerChannels[index];
						if (mixerOn[index] === turnOn) continue;
						const result = await deviceControlOnOff({ device: channel.device });
						if (result.success && nextStatus) {
							mixerOn[index] = result.expected_ison;
							nextStatus = patchOutput(channel.device, nextStatus, result.expected_ison);
							clear_device_timer_state(nextStatus, channel.device);
						} else {
							failed = true;
							if ($auth_locked_devices.indexOf(real_id(device.id)) === -1) {
								toast.open({
									type: 'danger',
									title: $format('error_global', { default: 'Something went wrong!' }),
								});
							}
							break;
						}
					}
					if (!failed && nextStatus) {
						hasTimer = false;
						mixerOn = [...mixerOn];
						devices_status.update_device_status({
							id: real_id(device.id),
							msg: nextStatus,
						});
					}
				} else {
					const result = await deviceControlOnOff({ device });
					if (result.success) {
						ison = result.expected_ison;
						applyTurnResult(device, ison);
					} else if ($auth_locked_devices.indexOf(real_id(device.id)) === -1) {
						toast.open({
							type: 'danger',
							title: $format('error_global', { default: 'Something went wrong!' }),
						});
					}
				}
			} finally {
				powerLoading = false;
			}
		});
	};

	const onHuePreview = (event: CustomEvent<{ value: number }>) => {
		dragging = 'hue';
		hue = event.detail.value;
	};
	const onSatPreview = (event: CustomEvent<{ value: number }>) => {
		dragging = 'sat';
		saturation = event.detail.value;
	};
	const onCtPreview = (event: CustomEvent<{ value: number }>) => {
		dragging = 'ct';
		ct = event.detail.value;
	};
	const onWhitePreview = (event: CustomEvent<{ value: number }>) => {
		dragging = 'white';
		white = event.detail.value;
	};
	const onBriPreview = (event: CustomEvent<{ value: number }>) => {
		dragging = 'bri';
		brightness = event.detail.value;
	};
	const onMixerBriPreview = (index: number, event: CustomEvent<{ value: number }>) => {
		dragging = `bri-${index}`;
		mixerBrightness[index] = event.detail.value;
		mixerBrightness = [...mixerBrightness];
	};

	const channelLabel = (index: number, name: string) =>
		name ||
		$format('label_channel_n', {
			default: 'Channel {n}',
			values: { n: index + 1 },
		});
</script>

<div class="light-priority-view d-flex flex-direction-column gap-s width-full">
	<ControlCard
		priority="wide"
		overflow="hidden"
		active={anyOn}
		accentColor={cardAccent}
		disabled={islandDisabled || !hasRights}
		stale={islandDisabled}
		a11yProps={{
			ariaLabel: isDimmer
				? $format('label_dimmer_brightness_control', {
						default: 'Dimmer brightness control',
					})
				: $format('label_light_color_controls', {
						default: 'Light color and brightness controls',
					}),
		}}
	>
		<div class="d-flex flex-direction-column gap-m width-full">
			{#if caps.showModeSwitch}
				<ButtonGroupV2
					variant="segmented"
					fullWidth
					noWrap
					aria-label={$format('label_rgb_ww_mode', { default: 'RGB or warm white' })}
				>
					<Button
						id="{controlId}-rgb"
						class={displayedColorType === 'color' ? 'active' : ''}
						size="m"
						aria-label={$format('button_rgb', { default: 'RGB' })}
						disabled={islandDisabled || modeLoading || !hasRights}
						on:click={() => handleMode('color')}
					>
						{$format('button_rgb', { default: 'RGB' })}
					</Button>
					<Button
						id="{controlId}-ww"
						class={displayedColorType === 'white' ? 'active' : ''}
						size="m"
						aria-label={$format('button_ww', { default: 'WW' })}
						disabled={islandDisabled || modeLoading || !hasRights}
						on:click={() => handleMode('white')}
					>
						{$format('button_ww', { default: 'WW' })}
					</Button>
				</ButtonGroupV2>

				<div class="light-priority-mode-rails">
					<LightModeRail open={displayedColorType === 'color'}>
						<div>
							<Text class="mb-xs" styleType="tiny">
								{$format('label_hue', { default: 'Hue' })}
								{hue}°
							</Text>
							<ColorSlider
								id="{controlId}-hue"
								min={0}
								max={360}
								value={hue}
								fill="none"
								gradient={HUE_GRADIENT}
								unit="°"
								disabled={railsDisabled || displayedColorType !== 'color'}
								a11yProps={{ ariaLabel: $format('label_hue', { default: 'Hue' }) }}
								on:preview={onHuePreview}
								on:value_change={(event) =>
									runOrUnlock(() => commitHueSat(event.detail.value, saturation))}
							/>
						</div>
						<div>
							<Text class="mb-xs" styleType="tiny">
								{$format('label_saturation', { default: 'Saturation' })}
								{saturation}%
							</Text>
							<ColorSlider
								id="{controlId}-sat"
								value={saturation}
								fill="value"
								gradient={saturationGradient}
								unit="%"
								disabled={railsDisabled || displayedColorType !== 'color'}
								a11yProps={{
									ariaLabel: $format('label_saturation', { default: 'Saturation' }),
								}}
								on:preview={onSatPreview}
								on:value_change={(event) =>
									runOrUnlock(() => commitHueSat(hue, event.detail.value))}
							/>
						</div>
					</LightModeRail>
					<LightModeRail open={displayedColorType === 'white'}>
						<div>
							<Text class="mb-xs" styleType="tiny">
								{$format('label_color_temp', { default: 'Color temperature' })}
								{ct} K
							</Text>
							<ColorSlider
								id="{controlId}-ct"
								min={caps.ctRange.minValue}
								max={caps.ctRange.maxValue}
								value={ct}
								fill="value"
								gradient={WARMTH_GRADIENT}
								unit="K"
								disabled={railsDisabled || displayedColorType !== 'white'}
								a11yProps={{
									ariaLabel: $format('label_color_temp', {
										default: 'Color temperature',
									}),
								}}
								on:preview={onCtPreview}
								on:value_change={(event) =>
									runOrUnlock(() => commitCt(event.detail.value))}
							/>
						</div>
					</LightModeRail>
				</div>
			{:else}
				{#if caps.showRgb}
					<div>
						<Text class="mb-xs" styleType="tiny">
							{$format('label_hue', { default: 'Hue' })}
							{hue}°
						</Text>
						<ColorSlider
							id="{controlId}-hue"
							min={0}
							max={360}
							value={hue}
							fill="none"
							gradient={HUE_GRADIENT}
							unit="°"
							disabled={railsDisabled}
							a11yProps={{ ariaLabel: $format('label_hue', { default: 'Hue' }) }}
							on:preview={onHuePreview}
							on:value_change={(event) =>
								runOrUnlock(() => commitHueSat(event.detail.value, saturation))}
						/>
					</div>
					<div>
						<Text class="mb-xs" styleType="tiny">
							{$format('label_saturation', { default: 'Saturation' })}
							{saturation}%
						</Text>
						<ColorSlider
							id="{controlId}-sat"
							value={saturation}
							fill="value"
							gradient={saturationGradient}
							unit="%"
							disabled={railsDisabled}
							a11yProps={{
								ariaLabel: $format('label_saturation', { default: 'Saturation' }),
							}}
							on:preview={onSatPreview}
							on:value_change={(event) =>
								runOrUnlock(() => commitHueSat(hue, event.detail.value))}
						/>
					</div>
				{/if}

				{#if caps.showCct}
					<div>
						<Text class="mb-xs" styleType="tiny">
							{$format('label_color_temp', { default: 'Color temperature' })}
							{ct} K
						</Text>
						<ColorSlider
							id="{controlId}-ct"
							min={caps.ctRange.minValue}
							max={caps.ctRange.maxValue}
							value={ct}
							fill="value"
							gradient={WARMTH_GRADIENT}
							unit="K"
							disabled={railsDisabled}
							a11yProps={{
								ariaLabel: $format('label_color_temp', { default: 'Color temperature' }),
							}}
							on:preview={onCtPreview}
							on:value_change={(event) => runOrUnlock(() => commitCt(event.detail.value))}
						/>
					</div>
				{/if}
			{/if}

			{#if caps.showWhite}
				<div>
					<Text class="mb-xs" styleType="tiny">
						{$format('label_white_channel', { default: 'White' })}
						{Math.round(white)}%
					</Text>
					<ColorSlider
						id="{controlId}-white"
						value={white}
						fill="value"
						gradient="linear-gradient(90deg, rgb(255 255 255 / 0.16), rgb(255 255 255))"
						unit="%"
						disabled={railsDisabled}
						a11yProps={{
							ariaLabel: $format('label_white_channel', { default: 'White' }),
						}}
						on:preview={onWhitePreview}
						on:value_change={(event) => runOrUnlock(() => commitWhite(event.detail.value))}
					/>
				</div>
			{/if}

			{#if isMixer}
				{#each mixerChannels as channel, index (channel.channel)}
					<div>
						<Text class="mb-xs" styleType="tiny">
							{channelLabel(index, channel.name)}
							{Math.round($displayedMixerBrightness[index] || 0)}%
						</Text>
						<ColorSlider
							id="{controlId}-bri-{channel.channel}"
							value={$displayedMixerBrightness[index] || 0}
							fill="value"
							gradient={brightnessGradient}
							unit="%"
							disabled={islandDisabled ||
								!displayMixerOn[index] ||
								!hasRights ||
								brightnessDisabled}
							a11yProps={{
								ariaLabel: channelLabel(index, channel.name),
							}}
							on:preview={(event) => onMixerBriPreview(index, event)}
							on:value_change={(event) =>
								runOrUnlock(() => commitMixerBrightness(index, event.detail.value))}
						/>
					</div>
				{/each}
			{:else}
				<div>
					<Text class="mb-xs" styleType="tiny">
						{$format('label_brightness', { default: 'Brightness' })}
						{Math.round($displayedBrightness)}%
					</Text>
					<ColorSlider
						id="{controlId}-bri"
						value={$displayedBrightness}
						fill="value"
						gradient={brightnessGradient}
						unit="%"
						disabled={railsDisabled}
						a11yProps={{
							ariaLabel: $format('label_brightness', { default: 'Brightness' }),
						}}
						on:preview={onBriPreview}
						on:value_change={(event) =>
							runOrUnlock(() => commitBrightness(event.detail.value))}
					/>
				</div>
			{/if}
		</div>
	</ControlCard>

	<ControlCard
		class="light-priority-power"
		priority="wide"
		overflow="hidden"
		active={false}
		accentColor={cardAccent}
		disabled={islandDisabled || !hasRights}
		stale={islandDisabled}
		a11yProps={{ ariaLabel: $format('label_power', { default: 'Power' }) }}
	>
		<div class="d-flex align-items-center justify-content-between width-full">
			<div class="d-flex align-items-center gap-s min-width-0">
				<Icon
					class="light-priority-bulb {anyOn ? 'is-on' : 'text-color-muted'}"
					icon="fa-solid fa-lightbulb"
					size="m"
				/>
				<div class="d-flex flex-direction-column min-width-0">
					<Text class="text-bold">
						{#if mixedPower}
							{$format('label_mixed_lights_on', {
								default: '{on} of {total}',
								values: { on: onCount, total: mixerChannels.length },
							})}
						{:else if anyOn}
							{$format('label_light_on', { default: 'On' })}
						{:else}
							{capFirstChar($format('label_off', { default: 'off' }))}
						{/if}
					</Text>
					<Text styleType="tiny" class="text-color-muted">
						{isDimmer
							? $format('label_dimmer_control', { default: 'Dimmer' })
							: $format('label_light_control', { default: 'Light' })}
					</Text>
				</div>
			</div>
			<DeviceButtonV2
				id="{controlId}-power"
				icon="fa fa-power-off"
				size="l"
				variant="primary"
				isActive={allOn}
				isLoading={powerLoading}
				{hasTimer}
				{timer}
				disabled={islandDisabled || !hasRights}
				aria-label={$format('label_turn_device', { default: 'Turn device' })}
				aria-pressed={allOn}
				a11yProps={{
					timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }),
					indicatorStatusText: mixedPower
						? $format('label_mixed_lights_on', {
								default: '{on} of {total}',
								values: { on: onCount, total: mixerChannels.length },
							})
						: allOn
							? $format('label_light_on', { default: 'On' })
							: capFirstChar($format('label_off', { default: 'off' })),
				}}
				on:click={(event) => {
					event.stopPropagation();
					handlePower();
				}}
			/>
		</div>
	</ControlCard>

	<PriorityGrid>
		<NextScheduleCard {device} stale={islandDisabled} disabled={islandDisabled} />
	</PriorityGrid>
</div>

{#if showPinModal}
	<PinUnlock
		bind:showModal={showPinModal}
		on:unlock={handlePinUnlock}
		on:cancel={() => {
			showPinModal = false;
			pendingAction = undefined;
		}}
	/>
{/if}

<style>
	.light-priority-view {
		min-width: 0;
	}

	.light-priority-mode-rails {
		display: grid;
		align-items: start;
		min-width: 0;
		overflow: hidden;
	}

	.light-priority-mode-rails > :global(*) {
		grid-area: 1 / 1;
		width: 100%;
		min-width: 0;
	}

	:global(.light-priority-power .light-priority-bulb.is-on i) {
		color: rgb(var(--lighting-accent-rgb));
	}
</style>
